import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiFileText,
  FiCamera,
  FiBook,
  FiHelpCircle,
  FiUsers,
  FiBarChart2,
  FiAward,
  FiChevronDown,
  FiRefreshCw,
  FiTrendingUp,
  FiClock,
  FiGrid,
  FiList,
  FiAlertCircle,
  FiPlus,
  FiBookmark,
} from "react-icons/fi";
import FeedPost from "../feed/FeedPost";
import { postApi } from "../../services/api/postApi";
import "./PostsTab.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Post type filters
const postTypeFilters = [
  { id: "all", label: "All Posts", icon: FiFileText },
  { id: "saved", label: "Saved", icon: FiBookmark, ownProfileOnly: true },
  { id: "text", label: "Text", icon: FiFileText },
  { id: "photo", label: "Photos", icon: FiCamera },
  { id: "research", label: "Research", icon: FiBook },
  { id: "question", label: "Questions", icon: FiHelpCircle },
  { id: "collaboration", label: "Collaboration", icon: FiUsers },
  { id: "poll", label: "Polls", icon: FiBarChart2 },
  { id: "milestone", label: "Milestones", icon: FiAward },
];

// Sort options
const sortOptions = [
  { id: "recent", label: "Most Recent", icon: FiClock },
  { id: "oldest", label: "Oldest First", icon: FiClock },
  { id: "popular", label: "Most Popular", icon: FiTrendingUp },
];

// Filter Chip Component
const FilterChip = ({ filter, isActive, onClick }) => {
  const Icon = filter.icon;

  return (
    <motion.button
      className={`pst-filter-chip ${isActive ? "active" : ""}`}
      onClick={() => onClick(filter.id)}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
    >
      <Icon className="pst-filter-icon" />
      <span>{filter.label}</span>
    </motion.button>
  );
};

// Stats Overview Component
const StatsOverview = ({ posts }) => {
  const stats = useMemo(() => {
    if (!posts?.length) return [];

    const counts = posts.reduce((acc, post) => {
      acc[post.postType] = (acc[post.postType] || 0) + 1;
      return acc;
    }, {});

    const totalLikes = posts.reduce((sum, p) => sum + (p.likeCount || 0), 0);
    const totalComments = posts.reduce(
      (sum, p) => sum + (p.commentCount || 0),
      0
    );

    return [
      { label: "Total Posts", value: posts.length, color: "var(--prf-blue)" },
      {
        label: "Research",
        value: counts.research || 0,
        color: "var(--prf-purple)",
      },
      {
        label: "Questions",
        value: counts.question || 0,
        color: "var(--prf-amber)",
      },
      {
        label: "Total Engagement",
        value: totalLikes + totalComments,
        color: "var(--prf-green)",
      },
    ];
  }, [posts]);

  return (
    <div className="pst-stats-overview">
      {stats.map((stat) => (
        <motion.div
          key={stat.label}
          className="pst-stat-card"
          variants={itemVariants}
          style={{ "--stat-color": stat.color }}
        >
          <span className="pst-stat-value">{stat.value}</span>
          <span className="pst-stat-label">{stat.label}</span>
        </motion.div>
      ))}
    </div>
  );
};

// Empty State Component
const EmptyState = ({ filter, isOwnProfile }) => (
  <motion.div
    className="pst-empty"
    variants={itemVariants}
    initial="hidden"
    animate="visible"
  >
    <div className="pst-empty-icon">
      <FiFileText />
    </div>
    <h3 className="pst-empty-title">
      {filter === "all" ? "No posts yet" : `No ${filter} posts yet`}
    </h3>
    <p className="pst-empty-text">
      {isOwnProfile
        ? "Share your thoughts, research, or questions with the community."
        : "This user hasn't posted anything yet."}
    </p>
    {isOwnProfile && (
      <motion.button
        className="pst-create-btn"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
      >
        <FiPlus />
        <span>Create Post</span>
      </motion.button>
    )}
  </motion.div>
);

// Error State Component
const ErrorState = ({ error, onRetry }) => (
  <motion.div
    className="pst-error"
    variants={itemVariants}
    initial="hidden"
    animate="visible"
  >
    <div className="pst-error-icon">
      <FiAlertCircle />
    </div>
    <h3 className="pst-error-title">Failed to load posts</h3>
    <p className="pst-error-text">{error}</p>
    <motion.button
      className="pst-retry-btn"
      onClick={onRetry}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
    >
      <FiRefreshCw />
      <span>Try Again</span>
    </motion.button>
  </motion.div>
);

// Loading Skeleton
const LoadingSkeleton = () => (
  <div className="pst-skeleton">
    {[1, 2, 3].map((i) => (
      <div key={i} className="pst-skeleton-card">
        <div className="pst-skeleton-header">
          <div className="pst-skeleton-avatar" />
          <div className="pst-skeleton-meta">
            <div className="pst-skeleton-name" />
            <div className="pst-skeleton-date" />
          </div>
        </div>
        <div className="pst-skeleton-content">
          <div className="pst-skeleton-line" />
          <div className="pst-skeleton-line short" />
        </div>
        <div className="pst-skeleton-actions">
          <div className="pst-skeleton-btn" />
          <div className="pst-skeleton-btn" />
          <div className="pst-skeleton-btn" />
        </div>
      </div>
    ))}
  </div>
);

// Main Component
const PostsTab = ({
  posts,
  postsLoading,
  postsError,
  postsPagination,
  postFilter,
  setPostFilter,
  postSort,
  setPostSort,
  loadPosts,
  isOwnProfile,
}) => {
  const [viewMode, setViewMode] = useState("list");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Saved posts state
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedPostsLoading, setSavedPostsLoading] = useState(false);
  const [savedPostsError, setSavedPostsError] = useState(null);
  const [savedPostsFetched, setSavedPostsFetched] = useState(false);

  // Fetch saved posts when filter is "saved"
  const fetchSavedPosts = useCallback(async () => {
    if (!isOwnProfile) return;
    
    setSavedPostsLoading(true);
    setSavedPostsError(null);
    
    try {
      const response = await postApi.getSavedPosts({ limit: 50 });
      const data = response.data;
      
      // Backend returns { success: true, posts: [...] }
      const extractedPosts = data.posts || data.data?.posts || [];
      
      setSavedPosts(extractedPosts);
      setSavedPostsFetched(true);
    } catch (error) {
setSavedPostsError(error.response?.data?.message || "Failed to load saved posts");
    } finally {
      setSavedPostsLoading(false);
    }
  }, [isOwnProfile]);

  // Fetch saved posts when filter changes to "saved"
  useEffect(() => {
    if (postFilter === "saved" && !savedPostsFetched && isOwnProfile) {
      fetchSavedPosts();
    }
  }, [postFilter, savedPostsFetched, isOwnProfile, fetchSavedPosts]);

  // Filter posts based on selected filter
  const filteredPosts = useMemo(() => {
    // For saved filter, return saved posts
    if (postFilter === "saved") {
      return savedPosts;
    }
    
    if (!posts?.length) return [];
    if (postFilter === "all") return posts;
    return posts.filter((p) => p.postType === postFilter);
  }, [posts, postFilter, savedPosts]);

  // Determine loading and error states based on filter
  const isLoading = postFilter === "saved" ? savedPostsLoading : postsLoading;
  const currentError = postFilter === "saved" ? savedPostsError : postsError;

  // Handle post update from FeedPost component
  const handlePostUpdate = () => {
    if (postFilter === "saved") {
      fetchSavedPosts();
    } else {
      loadPosts?.();
    }
  };

  // Handle post delete
  const handlePostDelete = () => {
    if (postFilter === "saved") {
      fetchSavedPosts();
    } else {
      loadPosts?.();
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    if (postFilter === "saved") {
      setSavedPostsFetched(false);
      fetchSavedPosts();
    } else {
      loadPosts?.();
    }
  };

  // Get current sort option
  const currentSort =
    sortOptions.find((s) => s.id === postSort) || sortOptions[0];
  const SortIcon = currentSort.icon;

  // Filter available filters based on profile ownership
  const availableFilters = useMemo(() => {
    return postTypeFilters.filter(filter => {
      if (filter.ownProfileOnly) return isOwnProfile;
      return true;
    });
  }, [isOwnProfile]);

  return (
    <motion.div
      className="pst-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Stats Overview */}
      {posts?.length > 0 && <StatsOverview posts={posts} />}

      {/* Toolbar */}
      <motion.div className="pst-toolbar" variants={itemVariants}>
        {/* Filter Chips */}
        <div className="pst-filters">
          <div className="pst-filters-scroll">
            {availableFilters.map((filter) => (
              <FilterChip
                key={filter.id}
                filter={filter}
                isActive={postFilter === filter.id}
                onClick={setPostFilter}
              />
            ))}
          </div>
        </div>

        {/* Toolbar Actions */}
        <div className="pst-toolbar-actions">
          {/* Sort Dropdown */}
          <div className="pst-sort-wrapper">
            <motion.button
              className="pst-sort-btn"
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SortIcon className="pst-sort-icon" />
              <span>{currentSort.label}</span>
              <FiChevronDown
                className={`pst-chevron ${showSortDropdown ? "open" : ""}`}
              />
            </motion.button>

            <AnimatePresence>
              {showSortDropdown && (
                <motion.div
                  className="pst-sort-dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {sortOptions.map((option) => {
                    const OptionIcon = option.icon;
                    return (
                      <button
                        key={option.id}
                        className={`pst-sort-option ${
                          postSort === option.id ? "active" : ""
                        }`}
                        onClick={() => {
                          setPostSort(option.id);
                          setShowSortDropdown(false);
                        }}
                      >
                        <OptionIcon />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* View Mode Toggle */}
          <div className="pst-view-toggle">
            <motion.button
              className={`pst-view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              whileTap={{ scale: 0.95 }}
            >
              <FiList />
            </motion.button>
            <motion.button
              className={`pst-view-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              whileTap={{ scale: 0.95 }}
            >
              <FiGrid />
            </motion.button>
          </div>

          {/* Refresh Button */}
          <motion.button
            className="pst-refresh-btn"
            onClick={handleRefresh}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isLoading}
          >
            <FiRefreshCw className={isLoading ? "spinning" : ""} />
          </motion.button>
        </div>
      </motion.div>

      {/* Content */}
      <div className="pst-content">
        {isLoading && filteredPosts.length === 0 ? (
          <LoadingSkeleton />
        ) : currentError ? (
          <ErrorState error={currentError} onRetry={handleRefresh} />
        ) : filteredPosts.length === 0 ? (
          <EmptyState filter={postFilter} isOwnProfile={isOwnProfile} />
        ) : (
          <motion.div
            className={`pst-posts ${
              viewMode === "grid" ? "grid-view" : "list-view"
            }`}
            variants={containerVariants}
          >
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post._id || post.id || index}
                className="pst-post-wrapper"
                variants={itemVariants}
              >
                <FeedPost
                  post={post}
                  onUpdate={handlePostUpdate}
                  onDelete={handlePostDelete}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Load More */}
      {postsPagination?.hasMore && !postsLoading && (
        <motion.div className="pst-load-more" variants={itemVariants}>
          <motion.button
            className="pst-load-more-btn"
            onClick={loadPosts}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {postsLoading ? (
              <>
                <FiRefreshCw className="spinning" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <span>Load More Posts</span>
                <FiChevronDown />
              </>
            )}
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PostsTab;
